<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { useI18n } from 'vue-i18n'
import paymentTemplateService from '../services/paymentTemplate.service'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

const loading = ref(true)
const saving = ref(false)

// Get club UUID from route
const clubUuid = computed(() => route.params.uuid as string)

// Template content
const templateContent = ref('')
const charCount = computed(() => templateContent.value.length)

// Default templates per language
const defaultTemplates: Record<string, string> = {
  pl: `Dzień dobry, przypominamy o konieczności uregulowania płatności.
Imię i nazwisko: {__IMIE_I_NAZWISKO__}
Tytuł płatności: {__TYTUL_PLATNOSCI__}
Wysokość płatności: {__KWOTA__}
Data płatności: {__DATA_PLATNOSCI__}

Link do płatności: {__URL_DO_PLATNOSCI__}

Pozdrawiamy,
{__NAZWA_KLUBU__}`,
  en: `Hello, this is a reminder about your payment.
Full name: {__IMIE_I_NAZWISKO__}
Payment title: {__TYTUL_PLATNOSCI__}
Payment amount: {__KWOTA__}
Payment date: {__DATA_PLATNOSCI__}

Payment link: {__URL_DO_PLATNOSCI__}

Best regards,
{__NAZWA_KLUBU__}`,
  de: `Guten Tag, wir erinnern Sie an Ihre ausstehende Zahlung.
Name: {__IMIE_I_NAZWISKO__}
Zahlungstitel: {__TYTUL_PLATNOSCI__}
Betrag: {__KWOTA__}
Zahlungsdatum: {__DATA_PLATNOSCI__}

Zahlungslink: {__URL_DO_PLATNOSCI__}

Mit freundlichen Grüßen,
{__NAZWA_KLUBU__}`,
  fr: `Bonjour, nous vous rappelons votre paiement en attente.
Nom complet : {__IMIE_I_NAZWISKO__}
Titre du paiement : {__TYTUL_PLATNOSCI__}
Montant : {__KWOTA__}
Date de paiement : {__DATA_PLATNOSCI__}

Lien de paiement : {__URL_DO_PLATNOSCI__}

Cordialement,
{__NAZWA_KLUBU__}`,
  es: `Hola, le recordamos su pago pendiente.
Nombre completo: {__IMIE_I_NAZWISKO__}
Título del pago: {__TYTUL_PLATNOSCI__}
Importe: {__KWOTA__}
Fecha de pago: {__DATA_PLATNOSCI__}

Enlace de pago: {__URL_DO_PLATNOSCI__}

Saludos cordiales,
{__NAZWA_KLUBU__}`,
  it: `Buongiorno, le ricordiamo il pagamento in sospeso.
Nome completo: {__IMIE_I_NAZWISKO__}
Titolo del pagamento: {__TYTUL_PLATNOSCI__}
Importo: {__KWOTA__}
Data di pagamento: {__DATA_PLATNOSCI__}

Link per il pagamento: {__URL_DO_PLATNOSCI__}

Cordiali saluti,
{__NAZWA_KLUBU__}`,
  pt: `Olá, lembramos do seu pagamento pendente.
Nome completo: {__IMIE_I_NAZWISKO__}
Título do pagamento: {__TYTUL_PLATNOSCI__}
Valor: {__KWOTA__}
Data de pagamento: {__DATA_PLATNOSCI__}

Link de pagamento: {__URL_DO_PLATNOSCI__}

Com os melhores cumprimentos,
{__NAZWA_KLUBU__}`,
  nl: `Goedendag, wij herinneren u aan uw openstaande betaling.
Volledige naam: {__IMIE_I_NAZWISKO__}
Betalingstitel: {__TYTUL_PLATNOSCI__}
Bedrag: {__KWOTA__}
Betaaldatum: {__DATA_PLATNOSCI__}

Betalingslink: {__URL_DO_PLATNOSCI__}

Met vriendelijke groet,
{__NAZWA_KLUBU__}`,
  cs: `Dobrý den, připomínáme Vám neuhrazenou platbu.
Jméno a příjmení: {__IMIE_I_NAZWISKO__}
Název platby: {__TYTUL_PLATNOSCI__}
Částka: {__KWOTA__}
Datum platby: {__DATA_PLATNOSCI__}

Odkaz na platbu: {__URL_DO_PLATNOSCI__}

S pozdravem,
{__NAZWA_KLUBU__}`,
  da: `Hej, vi minder dig om din udestående betaling.
Fulde navn: {__IMIE_I_NAZWISKO__}
Betalingstitel: {__TYTUL_PLATNOSCI__}
Beløb: {__KWOTA__}
Betalingsdato: {__DATA_PLATNOSCI__}

Betalingslink: {__URL_DO_PLATNOSCI__}

Med venlig hilsen,
{__NAZWA_KLUBU__}`,
  sv: `Hej, vi påminner dig om din utestående betalning.
Fullständigt namn: {__IMIE_I_NAZWISKO__}
Betalningstitel: {__TYTUL_PLATNOSCI__}
Belopp: {__KWOTA__}
Betalningsdatum: {__DATA_PLATNOSCI__}

Betalningslänk: {__URL_DO_PLATNOSCI__}

Med vänliga hälsningar,
{__NAZWA_KLUBU__}`,
  no: `Hei, vi minner deg om din utestående betaling.
Fullt navn: {__IMIE_I_NAZWISKO__}
Betalingstittel: {__TYTUL_PLATNOSCI__}
Beløp: {__KWOTA__}
Betalingsdato: {__DATA_PLATNOSCI__}

Betalingslenke: {__URL_DO_PLATNOSCI__}

Med vennlig hilsen,
{__NAZWA_KLUBU__}`,
  fi: `Hei, muistutamme sinua maksamattomasta maksustasi.
Koko nimi: {__IMIE_I_NAZWISKO__}
Maksun otsikko: {__TYTUL_PLATNOSCI__}
Summa: {__KWOTA__}
Maksupäivä: {__DATA_PLATNOSCI__}

Maksulinkki: {__URL_DO_PLATNOSCI__}

Ystävällisin terveisin,
{__NAZWA_KLUBU__}`,
  hu: `Tisztelt Tagunk, emlékeztetjük a függőben lévő fizetésre.
Teljes név: {__IMIE_I_NAZWISKO__}
Fizetés címe: {__TYTUL_PLATNOSCI__}
Összeg: {__KWOTA__}
Fizetési határidő: {__DATA_PLATNOSCI__}

Fizetési link: {__URL_DO_PLATNOSCI__}

Üdvözlettel,
{__NAZWA_KLUBU__}`,
  hr: `Poštovani, podsjećamo vas na nepodmireno plaćanje.
Puno ime: {__IMIE_I_NAZWISKO__}
Naziv plaćanja: {__TYTUL_PLATNOSCI__}
Iznos: {__KWOTA__}
Datum plaćanja: {__DATA_PLATNOSCI__}

Poveznica za plaćanje: {__URL_DO_PLATNOSCI__}

Srdačan pozdrav,
{__NAZWA_KLUBU__}`,
  lt: `Sveiki, primename apie neapmokėtą mokėjimą.
Vardas ir pavardė: {__IMIE_I_NAZWISKO__}
Mokėjimo pavadinimas: {__TYTUL_PLATNOSCI__}
Suma: {__KWOTA__}
Mokėjimo data: {__DATA_PLATNOSCI__}

Mokėjimo nuoroda: {__URL_DO_PLATNOSCI__}

Pagarbiai,
{__NAZWA_KLUBU__}`,
  ro: `Bună ziua, vă reamintim de plata restantă.
Nume complet: {__IMIE_I_NAZWISKO__}
Titlul plății: {__TYTUL_PLATNOSCI__}
Suma: {__KWOTA__}
Data plății: {__DATA_PLATNOSCI__}

Link de plată: {__URL_DO_PLATNOSCI__}

Cu stimă,
{__NAZWA_KLUBU__}`
}

// Default template - reactive based on language
const defaultTemplate = computed(() => {
  const locale = authStore.initData?.lang || 'pl'
  return defaultTemplates[locale] || defaultTemplates.en
})

// Preview content
const previewContent = computed(() => {
  if (!templateContent.value.trim()) {
    return ''
  }

  let content = templateContent.value

  // Replace placeholders with example values
  content = content.replace(/{__IMIE_I_NAZWISKO__}/g, `<strong>${t('paymentTemplate.exampleFullName')}</strong>`)
  content = content.replace(/{__IMIE__}/g, `<strong>${t('paymentTemplate.exampleFirstName')}</strong>`)
  content = content.replace(/{__NAZWISKO__}/g, `<strong>${t('paymentTemplate.exampleLastName')}</strong>`)
  content = content.replace(/{__KWOTA__}/g, `<strong>${t('paymentTemplate.exampleAmount')}</strong>`)
  content = content.replace(/{__DATA_PLATNOSCI__}/g, '<strong>2025-10-31</strong>')
  content = content.replace(/{__TYTUL_PLATNOSCI__}/g, `<strong>${t('paymentTemplate.examplePaymentTitle')}</strong>`)
  content = content.replace(/{__NAZWA_KLUBU__}/g, `<strong>${t('paymentTemplate.exampleClubName')}</strong>`)
  content = content.replace(/{__URL_DO_PLATNOSCI__}/g, '<strong>https://example.com/payment/abc123</strong>')

  // Convert newlines to <br>
  content = content.replace(/\n/g, '<br>')

  return content
})

// Placeholders
const placeholders = [
  { label: t('paymentTemplate.fullName'), value: '{__IMIE_I_NAZWISKO__}' },
  { label: t('paymentTemplate.firstName'), value: '{__IMIE__}' },
  { label: t('paymentTemplate.lastName'), value: '{__NAZWISKO__}' },
  { label: t('paymentTemplate.amount'), value: '{__KWOTA__}' },
  { label: t('paymentTemplate.paymentDate'), value: '{__DATA_PLATNOSCI__}' },
  { label: t('paymentTemplate.paymentTitle'), value: '{__TYTUL_PLATNOSCI__}' },
  { label: t('paymentTemplate.clubName'), value: '{__NAZWA_KLUBU__}' },
  { label: t('paymentTemplate.paymentUrl'), value: '{__URL_DO_PLATNOSCI__}' }
]

// Insert placeholder at cursor position
const insertPlaceholder = (placeholder: string) => {
  const textarea = document.getElementById('templateContent') as HTMLTextAreaElement
  if (!textarea) return

  const start = textarea.selectionStart
  const end = textarea.selectionEnd
  const text = templateContent.value

  // Insert placeholder at cursor position
  templateContent.value = text.substring(0, start) + placeholder + text.substring(end)

  // Move cursor after inserted placeholder
  setTimeout(() => {
    textarea.selectionStart = textarea.selectionEnd = start + placeholder.length
    textarea.focus()
  }, 0)
}

// Go back to club page
const goBack = () => {
  router.push('/payments')
}

// Save template
const saveTemplate = async () => {
  if (!templateContent.value.trim()) {
    toastStore.error(t('paymentTemplate.errorEmptyTemplate'))
    return
  }

  saving.value = true

  try {
    const response = await paymentTemplateService.updateTemplate(clubUuid.value, templateContent.value)
    const message = response?.message || t('paymentTemplate.templateSaved')
    toastStore.success(message)
  } catch (err: any) {
    console.error('Error saving template:', err)
    const errorMessage = err.response?.data?.message || t('paymentTemplate.errorSavingTemplate')
    toastStore.error(errorMessage)
  } finally {
    saving.value = false
  }
}

// Initialize
onMounted(async () => {
  loading.value = true

  try {
    const response = await paymentTemplateService.getTemplate(clubUuid.value)
    templateContent.value = response.template || defaultTemplate.value
  } catch (err: any) {
    console.error('Error fetching template:', err)
    toastStore.error(t('paymentTemplate.errorFetchingTemplate'))
    templateContent.value = defaultTemplate.value
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="max-w-3xl mx-auto px-4 py-4">
      <!-- Back Button -->
      <div class="mb-4">
        <button
          @click="goBack"
          class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('common.backToPayments') }}
        </button>
      </div>

      <!-- Page Header -->
      <div class="mb-4">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('paymentTemplate.title') }}</h1>
        <p class="text-sm text-gray-600 mt-1">{{ t('paymentTemplate.subtitle') }}</p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="bg-white border-2 border-gray-300 rounded shadow-sm p-8 text-center">
        <p class="text-gray-500">{{ t('paymentTemplate.loading') }}</p>
      </div>

      <!-- Edit Form -->
      <form v-else @submit.prevent="saveTemplate" class="bg-white border-2 border-gray-300 rounded shadow-sm p-4">

        <!-- Message Content -->
        <div class="mb-4">
          <label for="templateContent" class="block text-sm font-medium text-gray-700 mb-1">
            {{ t('paymentTemplate.messageContent') }} <span class="text-red-600">*</span>
          </label>
          <textarea
            id="templateContent"
            v-model="templateContent"
            required
            rows="12"
            class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-600 font-sans"
            :placeholder="t('paymentTemplate.messagePlaceholder')"
          ></textarea>
          <p class="text-xs text-gray-500 mt-1">{{ t('paymentTemplate.charCount') }}: <span>{{ charCount }}</span></p>
        </div>

        <!-- Placeholder Buttons -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">{{ t('paymentTemplate.insertVariable') }}:</label>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="placeholder in placeholders"
              :key="placeholder.value"
              type="button"
              @click="insertPlaceholder(placeholder.value)"
              class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
            >
              {{ placeholder.label }}
            </button>
          </div>
        </div>

        <!-- Preview -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ t('paymentTemplate.preview') }}
          </label>
          <div class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded bg-gray-50 text-gray-700 min-h-[200px] whitespace-pre-wrap">
            <span v-if="!previewContent" class="text-gray-400 italic">{{ t('paymentTemplate.previewPlaceholder') }}</span>
            <span v-else v-html="previewContent"></span>
          </div>
        </div>

        <!-- Submit Section -->
        <div class="pt-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="submit"
            :disabled="saving"
            class="py-2 px-4 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ saving ? t('paymentTemplate.saving') : t('paymentTemplate.saveChanges') }}
          </button>
        </div>
      </form>
    </div>
  </main>
</template>
